import React from "react";
import _ from "lodash";
import moment from "moment-timezone";
import { NetworkAPI } from "apis";
import LoadingUI from "components/common/LoadingUI";
import DateTimeFormatter from "components/common/DateTimeFormatter";
import SelectedStickyFooter from "components/common/SelectedStickyFooter";
import FirstLookRulesTable from "./FirstLookRulesTable";
import ChangeLogsStatus from "../ChangeLogsStatus";
import ChangeLogModal from "../ChangeLogModal";

import {
  PPFILTER,
  RULE_FILTER,
  PRICE,
  TYPE_FILTER,
  PRICING_RULE,
} from "../Constants";
import { inputClass } from "helpers/StyleClass";
import { setQueryString } from "helpers/QueryString";
import {
  getRuleOptions,
  getPPOptions,
  getPriceTypeOptions,
  getPriceOptions,
  parseRuleToFilter,
  isSectionEmpty,
} from "./FirstLookHelper";
import { getFilteredLogsByRule } from "../DataHelpers";

const buttonClass =
  "px-2 py-1 bg-gray-100 text-xs rounded border border-gray-400 hover:bg-gray-200 text-gray-900 font-semibold";

const buttonActionClass =
  "px-2 py-1 bg-gray-200 text-xs rounded border border-gray-400 whitespace-pre shadow hover:bg-gray-300 text-gray-900 font-semibold";

const buttonDisabledClass =
  "px-2 py-1 bg-gray-200 text-xs rounded border border-gray-200 whitespace-pre text-gray-400 font-semibold cursor-not-allowed";

class FirstLookRulesViewer extends React.Component {
  _isMounted = false;

  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      errMsg: "",

      rules: null, // show in json
      transformedRules: null, // show in table
      filteredRules: null,
      updatedAt: null,
      inputValue: "",
      selectedIds: [],

      isJsonView: false,

      typeOptions: null,
      ruleOptions: null,
      ppOptions: null,
      priceTypeOptions: null,
      priceOptions: null,
      customPrice: 0, // to filter not blocked

      currentFilter: {
        type: TYPE_FILTER.ALL,
        rule: RULE_FILTER.ALL,
        ppType: PPFILTER.ALL, // when Publisher Rules
        priceType: "ALL", // when Publisher Rules
        price: PRICE.ALL, // when Publisher Rules
      },

      isChangeModalOpen: false,
      logIds: [],
      activeChangeLogs: [],
      inactiveChangeLogs: [],

      logFrom: moment().subtract(29, "days").valueOf(),
    };

    this.getChangeLogs = this.getChangeLogs.bind(this);
    this.toggleJsonView = this.toggleJsonView.bind(this);
    this.handleForceRefresh = this.handleForceRefresh.bind(this);

    this.handleTypeFilterChanged = this.handleTypeFilterChanged.bind(this);
    this.handleRuleTypeFilterChanged =
      this.handleRuleTypeFilterChanged.bind(this);
    this.handlePPTypeFilterChanged = this.handlePPTypeFilterChanged.bind(this);
    this.handlePriceTypeFilterChanged =
      this.handlePriceTypeFilterChanged.bind(this);
    this.handlePriceChange = this.handlePriceChange.bind(this);

    this.handleImportSelected = this.handleImportSelected.bind(this);

    this.handleCheckLog = this.handleCheckLog.bind(this);
    this.handleCheckSelected = this.handleCheckSelected.bind(this);
  }

  async componentDidMount() {
    this._isMounted = true;
    this.setState({ isLoading: true });
    const { networkId } = this.props;
    if (!networkId) {
      this.setState({ errMsg: "Missing Network ID" });
      return;
    }

    try {
      const { rules, transformedRules, updatedAt } = await this._getRules({
        isForceRefresh: false,
      });

      this.getChangeLogs(rules);

      if (!this._isMounted) return;

      this.setState({
        rules,
        transformedRules,
        updatedAt,
        ...parseRuleToFilter(transformedRules, this.state.currentFilter, {
          currency: this.props.networkInfo.currency,
          customPrice: this.state.customPrice,
        }),

        isLoading: false,
      });
    } catch (err) {
      console.log(err);
      this.setState({
        isLoading: false,
        errMsg: err.toString(),
      });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  async getChangeLogs(flList) {
    const { networkId } = this.props;
    try {
      this.setState({ isLogLoading: true });
      const changeLogs = await NetworkAPI.getGamUprChangeLogs({
        networkId: _.toNumber(this.props.networkId),
        fromTimestamp: this.state.logFrom,
      });

      const allChangeLogsId = _.map(changeLogs || [], (log) => log.entityId);
      const flExternalIds = flList.map((fl) => fl.externalId);
      const missingIds = _.difference(allChangeLogsId, flExternalIds);

      const inactiveRules = [];
      if (missingIds.length > 0) {
        const { rules } = await NetworkAPI.getFirstLookRulesByIds({
          networkId,
          externalIds: missingIds,
        });
        inactiveRules.push(...rules);
      }

      if (!this._isMounted) return;
      this.setState({
        activeChangeLogs: getFilteredLogsByRule(changeLogs, flList),
        inactiveChangeLogs: getFilteredLogsByRule(changeLogs, inactiveRules),
        isLogLoading: false,
      });
    } catch (err) {
      console.log(err);
    }
  }

  async handleForceRefresh() {
    this.setState({
      isLoading: true,
      updatedAt: null,
      rules: null,
      transformedRules: null,
      activeChangeLogs:[],
      inactiveChangeLogs: [],
    });
    try {
      const currentFilter = _.cloneDeep(this.state.currentFilter);
      const { rules, transformedRules, updatedAt } = await this._getRules({
        isForceRefresh: true,
      });

      if (!this._isMounted) return;

      this.getChangeLogs(rules);

      this.setState({
        rules,
        transformedRules,
        updatedAt,

        currentFilter,

        // statusOptions,
        ...parseRuleToFilter(transformedRules, currentFilter, {
          currency: this.props.networkInfo.currency,
          customPrice: this.state.customPrice,
        }),

        isLoading: false,
      });
    } catch (err) {
      console.log(err);
      this.setState({
        isLoading: false,
        errMsg: err.toString(),
      });
    }
  }

  // Type filter
  handleTypeFilterChanged(typeOption) {
    const { currentFilter, customPrice } = this.state;
    const newFilter = {
      ...currentFilter,
      type: typeOption.key,
      rule: RULE_FILTER.ALL,
      ppType: PPFILTER.ALL,
      priceType: "ALL",
      price: PRICE.ALL,
    };

    const filteredRules = typeOption.list;
    const ruleOptions = getRuleOptions(filteredRules);

    // RULEFILTER.PUBLISHER_ONLY
    const ppOptions = getPPOptions(ruleOptions[2].list);
    const priceTypeOptions = getPriceTypeOptions(ruleOptions[2].list);
    const priceOptions = getPriceOptions(
      ruleOptions[2].list,
      customPrice,
      this.props.networkInfo.currency
    );

    this.setState({
      filteredRules,
      currentFilter: newFilter,
      ppOptions,
      priceTypeOptions,
      ruleOptions,
      priceOptions,
    });
  }

  // Rule filter
  handleRuleTypeFilterChanged(ruleOption) {
    const { currentFilter } = this.state;
    const newFilter = {
      ...currentFilter,
      rule: ruleOption.key,
      ppType: PPFILTER.ALL,
      priceType: "ALL",
      price: PRICE.ALL,
    };

    const filteredRules = ruleOption.list;

    this.setState({
      filteredRules,
      currentFilter: newFilter,
    });
  }

  // PPType filter
  handlePPTypeFilterChanged(ppTypeOption) {
    const { currentFilter, customPrice } = this.state;
    const newFilter = {
      ...currentFilter,
      ppType: ppTypeOption.key,
      priceType: "ALL",
      price: PRICE.ALL,
    };

    const filteredRules = ppTypeOption.list;

    const priceTypeOptions = getPriceTypeOptions(filteredRules);
    const priceOptions = getPriceOptions(
      filteredRules,
      customPrice,
      this.props.networkInfo.currency
    );

    this.setState({
      filteredRules,
      currentFilter: newFilter,
      ppTypeFilter: ppTypeOption.key,
      priceTypeOptions,
      priceOptions,
    });
  }

  // Price Type filter
  handlePriceTypeFilterChanged(priceTypeOption) {
    const { currentFilter, customPrice } = this.state;
    const newFilter = {
      ...currentFilter,
      priceType: priceTypeOption.key,
      price: PRICE.ALL,
    };

    const filteredRules = priceTypeOption.list;
    const priceOptions = getPriceOptions(
      filteredRules,
      customPrice,
      this.props.networkInfo.currency
    );

    this.setState({
      filteredRules,
      currentFilter: newFilter,
      priceOptions,
    });
  }

  // Price filter
  handlePriceFilterChanged(priceOption) {
    const { currentFilter } = this.state;
    const newFilter = {
      ...currentFilter,
      price: priceOption.key,
    };

    const filteredRules = priceOption.list;

    this.setState({
      filteredRules,
      currentFilter: newFilter,
    });
  }

  handlePriceChange(e) {
    this.setState({ customPrice: e.target.value }, () => {
      // Update price filter
      const newList = this.state.priceOptions.find(
        (pf) => pf.key === PRICE.ALL
      ).list;
      const priceOptions = getPriceOptions(
        newList,
        this.state.customPrice,
        this.props.networkInfo.currency
      );
      const customData = priceOptions.find((pf) => pf.key === PRICE.CUSTOM);

      this.setState({
        filteredRules: customData ? customData.list : [],
        priceOptions,
      });
    });
  }

  handleImportSelected() {
    const { selectedIds } = this.state;
    const { networkId } = this.props;
    const queryString = setQueryString(
      this.props,
      { importId: selectedIds },
      true
    );
    window.open(
      `${window.location.origin}/#/ops-mgmt/account/network/${networkId}/first-look-setting?${queryString}`
    );
  }

  handleCheckLog(id) {
    this.setState({
      isChangeModalOpen: true,
      logIds: [id],
    });
  }

  handleCheckSelected() {
    const { selectedIds } = this.state;
    this.setState({
      isChangeModalOpen: true,
      logIds: selectedIds,
    });
  }

  toggleJsonView() {
    this.setState({ isJsonView: !this.state.isJsonView });
  }

  async _getRules({ isForceRefresh = false }) {
    const { networkId } = this.props;
    const { rules, updatedAt } = await NetworkAPI.getFirstLookRules({
      networkId,
      isForceRefresh,
      status: "ACTIVE",
    });

    const { config } = await NetworkAPI.getRawNetworkFeatures({ networkId });
    const protectedFLIds = _.get(config, "publisherFLProtectionIds", []);
    const preservedFLIds = _.get(config, "preservedFLIds", []);

    const { first_look = {} } = await NetworkAPI.getNetworkResourceMapping({
      networkId,
    });

    const resourceById = _.keyBy(first_look.mapping, (item) => {
      return item.id;
    });

    const transformedRules = this._transformRules({
      rules,
      protectedFLIds,
      preservedFLIds,
      resourceById,
    });

    return {
      rules,
      transformedRules,
      updatedAt,
    };
  }

  _transformRules({ rules, protectedFLIds, preservedFLIds, resourceById }) {
    const transformedRules = _.map(rules, (rule) => {
      let targetingSection = {};
      if (rule.targetingSection.inventory) {
        if (!isSectionEmpty(rule.targetingSection.inventory.unitIds)) {
          _.set(
            targetingSection,
            ["inventory", "units"], // units not unitIds!
            rule.targetingSection.inventory.unitIds
          );
        }
        if (!isSectionEmpty(rule.targetingSection.inventory.placementIds)) {
          _.set(
            targetingSection,
            ["inventory", "placementIds"],
            rule.targetingSection.inventory.placementIds
          );
        }
        if (!isSectionEmpty(rule.targetingSection.inventory.mcm)) {
          _.set(
            targetingSection,
            ["inventory", "mcm"],
            rule.targetingSection.inventory.mcm
          );
        }
      }

      const targetingKeys = [
        "customs",
        "deviceCategory",
        "os",
        "country",
        "browser",
        "inventoryTypes",
        "videoPositions",
        "inventoryFormat",
        "inventoryUrl",
        "inventorySize",
        "mobileApplication",
      ];
      _.forEach(targetingKeys, (tk) => {
        if (!isSectionEmpty(rule.targetingSection[tk])) {
          targetingSection[tk] = rule.targetingSection[tk];
        }
      });

      rule.__isI2wRule = _.startsWith(rule.name, "Intowow_");
      rule.__isPubRule = !_.startsWith(rule.name, "Intowow_");
      rule.__isProtected =
        _.indexOf(protectedFLIds, _.parseInt(rule.externalId)) !== -1;
      rule.__isPreserved =
        _.indexOf(preservedFLIds, _.parseInt(rule.externalId)) !== -1;

      return {
        ...rule,
        targetingSection,
        props: _.get(resourceById, [rule.externalId, "props"], null),
      };
    });

    return transformedRules;
  }

  render() {
    const { networkId, networkInfo, units, virtualPlacements } = this.props;
    const {
      isLoading,
      errMsg,

      updatedAt,
      rules,
      filteredRules,
      inputValue,

      isJsonView,

      currentFilter,
      typeOptions,
      ruleOptions,
      ppOptions,
      priceTypeOptions,
      priceOptions,
      customPrice,

      selectedIds,

      isChangeModalOpen,
      activeChangeLogs,
      inactiveChangeLogs,
      isLogLoading,
      logFrom,
      logIds,
    } = this.state;

    const end = moment();
    const start = moment(updatedAt);
    const duration = moment.duration(end.diff(start));
    const updatedAgo = duration.asHours();

    const searchedRules = _.filter(filteredRules, (rule) => {
      return rule.name.toLowerCase().includes(inputValue.toLowerCase());
    });

    const disableCheckLogs = !_.some(selectedIds, (id) =>
      _.find(activeChangeLogs, { entityId: id })
    );

    return (
      <div>
        <div>
          <PageTitle title="First Look Rules"></PageTitle>

          {isLoading && <LoadingUI></LoadingUI>}
          {errMsg && <div className="text-red-800">{errMsg}</div>}

          {rules && (
            <>
              <div className="mb-2 flex items-center justify-end gap-2">
                <div className="mr-auto">
                  <ChangeLogsStatus
                    changeLogs={[...activeChangeLogs, ...inactiveChangeLogs]}
                    isLogLoading={isLogLoading}
                    logFrom={logFrom}
                    handleView={() =>
                      this.setState({ isChangeModalOpen: true, logIds: [] })
                    }
                  />
                </div>
                <div className="mr-2 text-sm">
                  Updated at:{" "}
                  <DateTimeFormatter
                    datetime={updatedAt}
                    timezone={networkInfo.timezone}
                  ></DateTimeFormatter>
                  {updatedAgo > 1 && (
                    <span className="pl-4 font-semibold text-red-600">
                      (Rules could be outdated)
                    </span>
                  )}
                </div>

                <button className={buttonClass} onClick={this.toggleJsonView}>
                  {!isJsonView ? "View original JSON" : "View in table"}
                </button>

                <button
                  className={buttonClass}
                  onClick={this.handleForceRefresh}
                >
                  Force Refresh
                </button>
              </div>

              {!isJsonView && (
                <>
                  {typeOptions && (
                    <div className="mt-2 flex flex-wrap items-center text-sm">
                      <div className="mr-1 font-semibold text-gray-700">
                        Type Filter:{" "}
                      </div>
                      {typeOptions.map((option) => {
                        const isSelected = currentFilter.type === option.key;
                        return (
                          <button
                            key={option.key}
                            type="button"
                            className={`border rounded mb-1 mr-1 border-gray-200 ${
                              isSelected ? "bg-blue-200" : "bg-white"
                            }  px-2 py-1 text-gray-800 hover:border-blue-400`}
                            onClick={() => {
                              this.handleTypeFilterChanged(option);
                            }}
                          >
                            {option.name} ({option.list.length})
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {ruleOptions && (
                    <div className="mt-2 flex flex-wrap items-center text-sm">
                      <div className="mr-1 font-semibold text-gray-700">
                        Rule Filter:{" "}
                      </div>
                      {ruleOptions.map((option) => {
                        const isSelected = option.key === currentFilter.rule;
                        return (
                          <button
                            key={option.key}
                            type="button"
                            className={`border rounded mb-1 mr-1 border-gray-200 font-medium ${
                              isSelected ? "bg-blue-200" : "bg-white"
                            }  px-2 py-1 text-gray-800 hover:border-blue-400`}
                            onClick={() => {
                              this.handleRuleTypeFilterChanged(option);
                            }}
                          >
                            {option.name} ({option.list.length})
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {currentFilter.rule === RULE_FILTER.PUBLISHER_ONLY && (
                    <>
                      <div className="mt-2 flex flex-wrap items-center text-sm">
                        <div className="mr-1 font-semibold text-gray-700">
                          Protected or Preserved Filter:{" "}
                        </div>
                        {(ppOptions || []).map((option) => {
                          const isSelected =
                            currentFilter.ppType === option.key;
                          return (
                            <button
                              key={option.name}
                              type="button"
                              className={`border rounded mb-1 mr-1 border-gray-200 ${
                                isSelected ? "bg-blue-200" : "bg-white"
                              }  px-2 py-1 text-gray-800 hover:border-blue-400`}
                              onClick={() => {
                                this.handlePPTypeFilterChanged(option);
                              }}
                            >
                              {option.name} ({option.list.length})
                            </button>
                          );
                        })}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center text-sm">
                        <div className="mr-1 font-semibold text-gray-700">
                          Price Type:{" "}
                        </div>
                        {(priceTypeOptions || []).map((option) => {
                          const isSelected =
                            currentFilter.priceType === option.key;
                          return (
                            <button
                              key={option.name}
                              type="button"
                              className={`border rounded mb-1 mr-1 border-gray-200 ${
                                isSelected ? "bg-blue-200" : "bg-white"
                              }  px-2 py-1 text-gray-800 hover:border-blue-400`}
                              onClick={() => {
                                this.handlePriceTypeFilterChanged(option);
                              }}
                            >
                              {option.name} ({option.list.length})
                            </button>
                          );
                        })}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center text-sm">
                        <div className="mr-1 font-semibold text-gray-700">
                          Price Filter:{" "}
                        </div>
                        {priceOptions.map((options) => {
                          const isSelected =
                            currentFilter.price === options.key;
                          return (
                            <button
                              key={options.key}
                              type="button"
                              className={`border rounded mb-1 mr-1 flex gap-1 border-gray-200 ${
                                isSelected ? "bg-blue-200" : "bg-white"
                              }  px-2 py-1 text-gray-800 hover:border-blue-400`}
                              onClick={() => {
                                this.handlePriceFilterChanged(options);
                              }}
                            >
                              {options.name}{" "}
                              {options.key === PRICE.CUSTOM && (
                                <input
                                  type="number"
                                  value={customPrice}
                                  className="border rounded focus:outline-none flex appearance-none border-gray-400 bg-white px-1 leading-normal hover:border-gray-500 focus:border-blue-400 focus:shadow-inner"
                                  style={{ width: "90px" }}
                                  onChange={this.handlePriceChange}
                                ></input>
                              )}{" "}
                              ({options.list.length})
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}

                  <div className="mt-2">
                    <input
                      type="text"
                      className={inputClass}
                      value={inputValue}
                      placeholder={"Search by rule name"}
                      onChange={(e) =>
                        this.setState({ inputValue: e.target.value })
                      }
                    ></input>
                  </div>
                </>
              )}

              <div className="flex w-full">
                <div className="w-full">
                  <div className="mb-1 mt-2 flex items-center justify-between">
                    <div className="text-lg font-semibold">
                      First Look Rules (
                      {isJsonView
                        ? (rules || []).length
                        : `${(searchedRules || []).length} of ${
                            (filteredRules || []).length
                          } rules`}
                      ):{" "}
                    </div>
                  </div>

                  {updatedAt && (
                    <>
                      {isJsonView ? (
                        <textarea
                          style={{ width: "100%", height: "600px" }}
                          defaultValue={JSON.stringify(rules, null, 4)}
                          readOnly
                        ></textarea>
                      ) : (
                        <div style={{ marginBottom: "600px" }}>
                          {networkInfo && searchedRules && (
                            <FirstLookRulesTable
                              items={searchedRules}
                              networkInfo={networkInfo}
                              networkId={networkId}
                              units={units}
                              relevantLogs={activeChangeLogs}
                              selectedItems={selectedIds}
                              virtualPlacements={virtualPlacements}
                              handleSelect={(list) =>
                                this.setState({ selectedIds: list })
                              }
                              handleCheckLog={this.handleCheckLog}
                            ></FirstLookRulesTable>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  <SelectedStickyFooter
                    idList={["externalId"]}
                    selectedItems={selectedIds}
                    handleClear={() => this.setState({ selectedIds: [] })}
                    actionButtons={
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={selectedIds.length === 0}
                          className={
                            selectedIds.length > 0
                              ? buttonActionClass
                              : buttonDisabledClass
                          }
                          onClick={this.handleImportSelected}
                        >
                          Import Selected
                        </button>
                        <button
                          type="button"
                          disabled={disableCheckLogs}
                          className={
                            !disableCheckLogs && selectedIds.length > 0
                              ? buttonActionClass
                              : buttonDisabledClass
                          }
                          onClick={this.handleCheckSelected}
                        >
                          Check Logs
                        </button>
                      </div>
                    }
                  ></SelectedStickyFooter>
                </div>
              </div>

              {isChangeModalOpen && (
                <ChangeLogModal
                  title="First Look Rule"
                  pricingRule={PRICING_RULE.FIRST_LOOK}
                  rules={rules}
                  changeLogs={[...activeChangeLogs, ...inactiveChangeLogs]}
                  networkId={networkId}
                  timezone={networkInfo.timezone}
                  logIds={logIds}
                  logFrom={logFrom}
                  onClose={() => this.setState({ isChangeModalOpen: false })}
                ></ChangeLogModal>
              )}
            </>
          )}
        </div>
      </div>
    );
  }
}

function PageTitle({ title }) {
  return <div className="text-3xl font-extrabold">{title}</div>;
}

export default FirstLookRulesViewer;
